import { useParams } from 'react-router-dom';
import { BillingRulesList } from './BillingRulesList';
import { useToast } from '@chakra-ui/react';
import { withPermission } from '../../withPermission';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type {
  ProviderInvoiceRuleFormValues,
  ProviderInvoiceRuleCreatePayload,
} from '@duke-hq/billing/types/rules';
import { formToCreatePayload } from '@duke-hq/svc-api/lib/billing/mappers/rules';

export const BillingRulesPage = withPermission('canViewProviders', () => {
  const { providerId } = useParams<{
    providerId: string;
    consultantId: string;
  }>();
  const utils = trpcAdmin.useUtils();

  const toast = useToast();

  const { data, isLoading, error } = trpcAdmin.finances.billing.listRulesByProvider.useQuery({
    providerId: Number(providerId),
  });

  const { data: courses } = trpcAdmin.courses.listCourses.useQuery({
    filters: { status: 'PUBLISHED' },
  });

  const createRule = trpcAdmin.finances.billing.createRule.useMutation({
    onSuccess: async () => {
      await utils.finances.billing.listRulesByProvider.invalidate({
        providerId: Number(providerId),
      });
    },
  });
  const updateRule = trpcAdmin.finances.billing.updateRule.useMutation({
    onSuccess: async () => {
      await utils.finances.billing.listRulesByProvider.invalidate({
        providerId: Number(providerId),
      });
    },
  });

  const handleCreate = async (values: ProviderInvoiceRuleFormValues) => {
    const payload: ProviderInvoiceRuleCreatePayload = formToCreatePayload(values);
    await createRule.mutateAsync(payload);
  };

  const handleUpdate = async (id: string, values: ProviderInvoiceRuleFormValues) => {
    const payload: ProviderInvoiceRuleCreatePayload = formToCreatePayload(values);
    await updateRule.mutateAsync({ id, ...payload });
  };

  const handleToggleActive = async (id: string, active: boolean) => {
    await updateRule.mutateAsync({ id, active });
    toast({ status: 'success', title: active ? 'Rule enabled' : 'Rule disabled' });
  };

  const handleSoftDelete = async (id: string) => {
    await updateRule.mutateAsync({ id, deletedAt: new Date(), active: false });
    toast({ status: 'success', title: 'Rule deleted' });
  };

  return (
    <BillingRulesList
      providerId={Number(providerId)}
      rules={data ?? []}
      loading={isLoading}
      error={error ? 'Failed to load rules' : null}
      courses={courses ?? []}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onToggleActive={handleToggleActive}
      onSoftDelete={handleSoftDelete}
    />
  );
});
